import React, { ReactNode, useState } from "react";
import { graphql, useMutation } from "react-relay";
import { Button } from "src/components/ui/button";
import { Input } from "src/components/ui/input";
import { Label } from "src/components/ui/label";
import { SecretInput } from "src/components/ui/secret-input";
import { ErrorAlert, SuccessAlert } from "src/components/ui/status-alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "src/components/ui/tabs";
import { Textarea } from "src/components/ui/textarea";
import { Tooltip } from "src/components/ui/tooltip";
import { usePermissions } from "src/context/MeContext";

import { IntegrationType } from "./__generated__/IntegrationsSection_organization.graphql";
import { TemporalIntegrationFormUpdateIntegrationMutation } from "./__generated__/TemporalIntegrationFormUpdateIntegrationMutation.graphql";
import { TemporalIntegrationFormValidateIntegrationMutation } from "./__generated__/TemporalIntegrationFormValidateIntegrationMutation.graphql";

const updateIntegrationMutation = graphql`
  mutation TemporalIntegrationFormUpdateIntegrationMutation(
    $input: UpdateIntegrationInput!
  ) {
    updateIntegration(input: $input) {
      integration {
        id
        name
        integrationType
        credentials
      }
      error
    }
  }
`;

const validateIntegrationMutation = graphql`
  mutation TemporalIntegrationFormValidateIntegrationMutation(
    $input: ValidateIntegrationInput!
  ) {
    validateIntegration(input: $input) {
      success
      error
    }
  }
`;

export type TemporalAuthTypeType = "API_KEY" | "MTLS";

interface TemporalIntegrationFormProps {
  onComplete: (success: boolean) => void;
  onCancel: () => void;
  onAuthTypeChange?: (authType: TemporalAuthTypeType) => void;
  initialCredentials?: Record<string, any> | null;
  integrationId?: string;
  leftSlot?: ReactNode;
}

// Treat redacted secret values (bullets or "[redacted]") as empty so the user
// can either re-enter them or leave them blank to keep the existing value.
const unwrapRedacted = (value: any): string => {
  if (value === null || value === undefined) return "";
  const s = String(value);
  if (s === "[redacted]" || /^•+$/.test(s)) return "";
  return s;
};

export default function TemporalIntegrationForm({
  onComplete,
  onCancel,
  onAuthTypeChange,
  initialCredentials,
  integrationId,
  leftSlot
}: TemporalIntegrationFormProps) {
  const isEditMode = !!integrationId;
  const initialAuthType = (initialCredentials?.auth_type === "MTLS" ? "MTLS" : "API_KEY") as TemporalAuthTypeType;
  const [authType, setAuthType] = useState<TemporalAuthTypeType>(initialAuthType);
  const [apiKey, setApiKey] = useState(unwrapRedacted(initialCredentials?.api_key));
  // In edit mode, leave cert/key blank so users can keep existing values without re-uploading.
  // Backend's _merge_credentials preserves any field omitted from the update payload.
  const [clientCertPem, setClientCertPem] = useState(isEditMode ? "" : unwrapRedacted(initialCredentials?.client_cert_pem));
  const [clientPrivateKeyPem, setClientPrivateKeyPem] = useState(unwrapRedacted(initialCredentials?.client_private_key_pem));
  const [serverRootCaCertPem, setServerRootCaCertPem] = useState(isEditMode ? "" : unwrapRedacted(initialCredentials?.server_root_ca_cert_pem));
  const [namespace, setNamespace] = useState(initialCredentials?.namespace ?? "");
  const [namespaceGrpcEndpoint, setNamespaceGrpcEndpoint] = useState(initialCredentials?.namespace_grpc_endpoint ?? "");
  const [formError, setFormError] = useState<string | null>(null);
  const [validationSuccess, setValidationSuccess] = useState<string | null>(null);
  const [isValidated, setIsValidated] = useState(false);
  const { canWrite } = usePermissions();
  const canModify = canWrite("integrations");

  const [commitMutation, isMutationInFlight] = useMutation<TemporalIntegrationFormUpdateIntegrationMutation>(
    updateIntegrationMutation
  );
  const [commitValidateMutation, isValidationInFlight] = useMutation<TemporalIntegrationFormValidateIntegrationMutation>(
    validateIntegrationMutation
  );

  const resetValidation = () => {
    setIsValidated(false);
    setValidationSuccess(null);
  };

  const buildCredentials = (): Record<string, string> | null => {
    if (authType === "API_KEY") {
      if (!isEditMode && !apiKey.trim()) {
        setFormError("API Key is required");
        return null;
      }
      const creds: Record<string, string> = { auth_type: "API_KEY" };
      if (apiKey.trim()) creds.api_key = apiKey.trim();
      return creds;
    }
    const missing: string[] = [];
    if (!namespace.trim()) missing.push("Namespace");
    if (!namespaceGrpcEndpoint.trim()) missing.push("Namespace gRPC endpoint");
    if (!isEditMode) {
      if (!clientCertPem.trim()) missing.push("Client certificate");
      if (!clientPrivateKeyPem.trim()) missing.push("Client private key");
    }
    if (missing.length) {
      setFormError(`Required: ${missing.join(", ")}`);
      return null;
    }
    const credentials: Record<string, string> = {
      auth_type: "MTLS",
      namespace: namespace.trim(),
      namespace_grpc_endpoint: namespaceGrpcEndpoint.trim()
    };
    if (clientCertPem.trim()) credentials.client_cert_pem = clientCertPem.trim();
    if (clientPrivateKeyPem.trim()) credentials.client_private_key_pem = clientPrivateKeyPem.trim();
    if (serverRootCaCertPem.trim()) {
      credentials.server_root_ca_cert_pem = serverRootCaCertPem.trim();
    }
    return credentials;
  };

  const handleTest = () => {
    setFormError(null);
    setValidationSuccess(null);
    setIsValidated(false);

    const credentials = buildCredentials();
    if (!credentials) return;

    const input: { integrationType: IntegrationType; credentials: string; integrationId?: string } = {
      integrationType: "TEMPORAL" as IntegrationType,
      credentials: JSON.stringify(credentials)
    };
    if (integrationId) input.integrationId = integrationId;

    commitValidateMutation({
      variables: { input },
      onCompleted: (response, errors) => {
        if (errors && errors.length > 0) {
          setFormError(errors.map(e => e.message).join(", "));
          return;
        }
        if (!response.validateIntegration.success) {
          setFormError(response.validateIntegration.error || "Validation failed");
          return;
        }
        setValidationSuccess("Credentials validated successfully.");
        setIsValidated(true);
      },
      onError: error => setFormError(error.message)
    });
  };

  const handleSave = () => {
    setFormError(null);
    setValidationSuccess(null);

    const credentials = buildCredentials();
    if (!credentials) return;

    const input: { integrationType: IntegrationType; credentials: string; integrationId?: string } = {
      integrationType: "TEMPORAL" as IntegrationType,
      credentials: JSON.stringify(credentials)
    };
    if (integrationId) input.integrationId = integrationId;

    commitMutation({
      variables: { input },
      onCompleted: (response, errors) => {
        if (errors && errors.length > 0) {
          setFormError(errors.map(e => e.message).join(", "));
          onComplete(false);
          return;
        }
        if (response.updateIntegration.error) {
          setFormError(response.updateIntegration.error);
          onComplete(false);
          return;
        }
        onComplete(true);
      },
      onError: error => {
        setFormError(error.message);
        onComplete(false);
      }
    });
  };

  const handleAuthTypeChange = (next: string) => {
    const nextAuthType = next as TemporalAuthTypeType;
    setAuthType(nextAuthType);
    setFormError(null);
    resetValidation();
    onAuthTypeChange?.(nextAuthType);
  };

  return (
    <div className="space-y-6">
      <div className="border-t border-border pt-4 space-y-4">
        <h3 className="text-base font-semibold text-foreground">Authentication</h3>
        <Tabs value={authType} onValueChange={isEditMode ? undefined : handleAuthTypeChange}>
          <TabsList>
            <TabsTrigger value="API_KEY" disabled={isEditMode && authType !== "API_KEY"}>API Key</TabsTrigger>
            <TabsTrigger value="MTLS" disabled={isEditMode && authType !== "MTLS"}>Certificate (mTLS)</TabsTrigger>
          </TabsList>
          {isEditMode && (
            <p className="text-xs text-muted-foreground pt-2">
              Authentication type cannot be changed after the integration is connected. Disconnect and reconnect to switch.
            </p>
          )}

          <TabsContent value="API_KEY" className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label htmlFor="api-key" className="text-muted-foreground">API Key</Label>
              <Tooltip
                content={!canModify ? "You don't have permission to modify integrations" : null}
                position="top"
                asChild
              >
                <span className="block w-full">
                  <SecretInput
                    id="api-key"
                    value={apiKey}
                    onChange={(e) => {
                      setApiKey(e.target.value);
                      resetValidation();
                    }}
                    placeholder={isEditMode ? "Leave blank to keep current value" : "Enter your Temporal API Key"}
                    disabled={!canModify}
                  />
                </span>
              </Tooltip>
              <p className="text-xs text-muted-foreground">
                Use an account-level API key with permissions to list namespaces.
              </p>
            </div>
          </TabsContent>

          <TabsContent value="MTLS" className="space-y-4 pt-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="namespace" className="text-muted-foreground">Namespace</Label>
                <Input
                  id="namespace"
                  value={namespace}
                  onChange={(e) => {
                    setNamespace(e.target.value);
                    resetValidation();
                  }}
                  placeholder="my-namespace.acct"
                  disabled={!canModify}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="namespace-grpc-endpoint" className="text-muted-foreground">
                  Namespace gRPC endpoint
                </Label>
                <Input
                  id="namespace-grpc-endpoint"
                  value={namespaceGrpcEndpoint}
                  onChange={(e) => {
                    setNamespaceGrpcEndpoint(e.target.value);
                    resetValidation();
                  }}
                  placeholder="my-namespace.acct.tmprl.cloud:7233"
                  disabled={!canModify}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="client-cert-pem" className="text-muted-foreground">Client certificate (PEM)</Label>
              <Textarea
                id="client-cert-pem"
                value={clientCertPem}
                onChange={(e) => {
                  setClientCertPem(e.target.value);
                  resetValidation();
                }}
                placeholder={isEditMode ? "Leave blank to keep current value" : "-----BEGIN CERTIFICATE-----\n...\n-----END CERTIFICATE-----"}
                rows={6}
                disabled={!canModify}
                className="font-mono text-xs"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="client-private-key-pem" className="text-muted-foreground">Client private key (PEM)</Label>
              <Textarea
                id="client-private-key-pem"
                value={clientPrivateKeyPem}
                onChange={(e) => {
                  setClientPrivateKeyPem(e.target.value);
                  resetValidation();
                }}
                placeholder={isEditMode ? "Leave blank to keep current value" : "-----BEGIN PRIVATE KEY-----\n...\n-----END PRIVATE KEY-----"}
                rows={6}
                disabled={!canModify}
                className="font-mono text-xs"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="server-root-ca-cert-pem" className="text-muted-foreground">
                Server root CA certificate (PEM, optional)
              </Label>
              <Textarea
                id="server-root-ca-cert-pem"
                value={serverRootCaCertPem}
                onChange={(e) => {
                  setServerRootCaCertPem(e.target.value);
                  resetValidation();
                }}
                placeholder={isEditMode ? "Leave blank to keep current value" : "Only required if your Temporal Cloud namespace uses a custom CA."}
                rows={4}
                disabled={!canModify}
                className="font-mono text-xs"
              />
            </div>

            <p className="text-xs text-muted-foreground">
              Temporal Cloud certificates are scoped to a single namespace.
            </p>
          </TabsContent>
        </Tabs>
      </div>

      {formError && <ErrorAlert icon="none">{formError}</ErrorAlert>}

      {validationSuccess && <SuccessAlert icon="none">{validationSuccess}</SuccessAlert>}

      <div className="flex justify-between items-center pt-2">
        <div>{leftSlot}</div>
        <div className="flex space-x-3">
        <Button variant="outline" onClick={onCancel} disabled={isMutationInFlight || isValidationInFlight}>
          Cancel
        </Button>
        <Tooltip
          content={!canModify ? "You don't have permission to connect integrations" : null}
          position="top"
        >
          <span className="inline-block">
            <Button
              variant="outline"
              onClick={handleTest}
              disabled={isMutationInFlight || isValidationInFlight || !canModify}
            >
              {isValidationInFlight ? "Testing..." : "Test Connection"}
            </Button>
          </span>
        </Tooltip>
        <div className="relative group">
          <Tooltip
            content={!canModify ? "You don't have permission to connect integrations" : null}
            position="top"
          >
            <span className="inline-block">
              <Button
                onClick={handleSave}
                disabled={!isValidated || isMutationInFlight || isValidationInFlight || !canModify}
              >
                {isMutationInFlight ? "Saving..." : isEditMode ? "Save Changes" : "Connect"}
              </Button>
            </span>
          </Tooltip>
          {!isValidated && !isMutationInFlight && !isValidationInFlight && canModify && (
            <div className="absolute bottom-full mb-2 right-0 px-3 py-2 bg-card text-foreground text-sm rounded opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity whitespace-nowrap">
              To connect, please test the connection.
            </div>
          )}
        </div>
        </div>
      </div>
    </div>
  );
}
